// src/components/subscriptions/StaffSelectionModal.js
import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../services/api';
import './StaffSelectionModal.css';

const StaffSelectionModal = ({
    requiredCount,
    selectedStaff = [],
    organizationId,
    onSelect,
    onClose
}) => {
    const [allStaff, setAllStaff] = useState([]);
    const [selected, setSelected] = useState(selectedStaff.map(s => s.id));
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all'); // all, selected, unselected

    useEffect(() => {
        loadStaff();
    }, [organizationId]);

    const loadStaff = async () => {
        try {
            setLoading(true);
            const response = await apiRequest.get('/staff', {
                params: {
                    organization_id: organizationId,
                    status: 'active',
                    limit: 1000
                }
            });
            // API wraps response as { success, data: { staff: [], pagination: {} } }
            setAllStaff(response.data.data?.staff || []);
        } catch (error) {
            console.error('Failed to load staff:', error);
            alert('Failed to load staff list');
        } finally {
            setLoading(false);
        }
    };

    const toggleStaffSelection = (staffId) => {
        setSelected(prev => {
            if (prev.includes(staffId)) {
                return prev.filter(id => id !== staffId);
            } else {
                if (prev.length >= requiredCount) {
                    alert(`You can only select ${requiredCount} staff members`);
                    return prev;
                }
                return [...prev, staffId];
            }
        });
    };

    const handleSelectAll = () => {
        const filtered = getFilteredStaff();
        const toSelect = filtered.slice(0, requiredCount).map(s => s.id);
        setSelected(toSelect);
    };

    const handleClearSelection = () => {
        setSelected([]);
    };

    const handleConfirm = () => {
        if (selected.length !== requiredCount) {
            alert(`Please select exactly ${requiredCount} staff members`);
            return;
        }

        const selectedStaffData = allStaff.filter(s => selected.includes(s.id));
        onSelect(selectedStaffData);
    };

    const getFilteredStaff = () => {
        let filtered = allStaff;

        // Apply search
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(staff =>
                (staff.name || '').toLowerCase().includes(query) ||
                (staff.username || '').toLowerCase().includes(query) ||
                (staff.email || '').toLowerCase().includes(query) ||
                (staff.designation || '').toLowerCase().includes(query) ||
                (staff.staff_id || '').toLowerCase().includes(query)
            );
        }

        // Apply filter
        if (filter === 'selected') {
            filtered = filtered.filter(staff => selected.includes(staff.id));
        } else if (filter === 'unselected') {
            filtered = filtered.filter(staff => !selected.includes(staff.id));
        }

        return filtered;
    };

    const filteredStaff = getFilteredStaff();

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="staff-selection-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Select Staff Members</h3>
                    <button className="close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="selection-info">
                    <div className="selection-count">
                        <span className="selected-number">{selected.length}</span>
                        <span className="separator">/</span>
                        <span className="required-number">{requiredCount}</span>
                        <span className="label">selected</span>
                    </div>

                    <div className="selection-actions">
                        <button
                            className="action-btn"
                            onClick={handleSelectAll}
                            disabled={filteredStaff.length < requiredCount}
                        >
                            <i className="fas fa-check-double"></i>
                            Select First {requiredCount}
                        </button>
                        <button
                            className="action-btn"
                            onClick={handleClearSelection}
                            disabled={selected.length === 0}
                        >
                            <i className="fas fa-times-circle"></i>
                            Clear All
                        </button>
                    </div>
                </div>

                <div className="modal-filters">
                    <div className="search-box">
                        <i className="fas fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search staff by name, email, department..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="filter-tabs">
                        <button
                            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            All ({allStaff.length})
                        </button>
                        <button
                            className={`filter-tab ${filter === 'selected' ? 'active' : ''}`}
                            onClick={() => setFilter('selected')}
                        >
                            Selected ({selected.length})
                        </button>
                        <button
                            className={`filter-tab ${filter === 'unselected' ? 'active' : ''}`}
                            onClick={() => setFilter('unselected')}
                        >
                            Unselected ({allStaff.length - selected.length})
                        </button>
                    </div>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading staff...</p>
                        </div>
                    ) : filteredStaff.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-users-slash"></i>
                            <p>No staff found</p>
                        </div>
                    ) : (
                        <div className="staff-list">
                            {filteredStaff.map((staff) => (
                                <div
                                    key={staff.id}
                                    className={`staff-item ${selected.includes(staff.id) ? 'selected' : ''}`}
                                    onClick={() => toggleStaffSelection(staff.id)}
                                >
                                    <div className="staff-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(staff.id)}
                                            onChange={() => { }}
                                        />
                                        <span className="checkmark"></span>
                                    </div>

                                    <div className="staff-avatar">
                                        {staff.image ? (
                                            <img src={staff.image} alt={staff.name} />
                                        ) : (
                                            <i className="fas fa-user"></i>
                                        )}
                                    </div>

                                    <div className="staff-details">
                                        <div className="staff-name">{staff.name}</div>
                                        <div className="staff-info">
                                            <span className="staff-email">
                                                <i className="fas fa-id-badge"></i>
                                                {staff.staff_id || staff.username}
                                            </span>
                                            {staff.designation && (
                                                <span className="staff-department">
                                                    <i className="fas fa-briefcase"></i>
                                                    {staff.designation}
                                                </span>
                                            )}
                                            {staff.premises_name && (
                                                <span className="staff-position">
                                                    <i className="fas fa-building"></i>
                                                    {staff.premises_name}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {selected.includes(staff.id) && (
                                        <div className="selected-badge">
                                            <i className="fas fa-check-circle"></i>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="confirm-btn"
                        onClick={handleConfirm}
                        disabled={selected.length !== requiredCount}
                    >
                        Confirm Selection ({selected.length}/{requiredCount})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StaffSelectionModal;